// Password validation utility functions

export const PASSWORD_REQUIREMENTS_TEXT = 
  'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character (!@#$%^&*...).';

/**
 * Validates password strength
 * @param {string} password - The password to validate
 * @returns {object} - Object containing isValid flag and message
 */
export const validatePasswordStrength = (password) => {
  if (!password || typeof password !== 'string') {
    return {
      isValid: false,
      message: 'Password is required'
    };
  }

  // Check minimum length
  if (password.length < 8) {
    return {
      isValid: false,
      message: 'Password must be at least 8 characters long'
    };
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one uppercase letter'
    };
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one lowercase letter'
    };
  }

  // Check for number
  if (!/\d/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one number'
    };
  }

  // Check for special character
  if (!/[!@#$%^&*()_+\-={};':"\\|,.<>/?]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one special character'
    };
  }

  return {
    isValid: true,
    message: 'Password is strong'
  };
};

/**
 * Returns an object with individual criteria checks for real-time feedback
 * @param {string} password - The password to evaluate
 * @returns {object} - Object indicating pass/fail for each criteria
 */
export const getPasswordCriteria = (password = '') => {
  const pwd = typeof password === 'string' ? password : '';
  return {
    minLength: pwd.length >= 8,
    hasUpper: /[A-Z]/.test(pwd),
    hasLower: /[a-z]/.test(pwd),
    hasNumber: /\d/.test(pwd),
    hasSpecial: /[!@#$%^&*()_+\-={};':"\\|,.<>/?]/.test(pwd),
    isValid: validatePasswordStrength(pwd).isValid
  };
};

/**
 * Checks if password meets minimum requirements
 * @param {string} password - The password to check
 * @returns {boolean} - True if password meets requirements
 */
export const isPasswordValid = (password) => {
  return validatePasswordStrength(password).isValid;
};